import React, { useEffect, useState } from "react";
import axios from "axios";
import ProfileHeader from "../ProfileHeader";
import { Container, Grid, Paper, Dialog, IconButton } from "@mui/material";
import Reviews from "../../Components/Reviews";
import PhotoLibrary from "../../PhotoLibrary.tsx/PhotoLibrary";
import AnalyticsBA from "../../Analytics/BA/AnalyticsBA";
import { ProfileSectionType } from "../ProfileSections";
import Events from "../../Events/Events";
import CloseIcon from "@mui/icons-material/Close";

interface ProfileBAProps {
  baId: any; // Define the type of baId as needed
  username: string;
  isModal?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
}

interface AmbassadorProfile {
  avatar_url?: string;
  first_name?: string;
  last_name?: string;
  city?: string;
  state_code?: string;
  bio?: string;
  ratings?: any;
  avg_engagement?: number;
  avg_expertise?: number;
  avg_overall?: number;
  avg_professionalism?: number;
  avg_sales?: number;
  username_id?: number;
}

const ProfileBA: React.FC<ProfileBAProps> = ({
  baId,
  username,
  isModal = false,
  isOpen = true,
  onClose,
}) => {
  const [ambassadorProfile, setAmbassadorProfile] =
    useState<AmbassadorProfile | null>(null);
  const [analyticsData, setAnalyticsData] = useState([]);
  const [salesOverviewData, setSalesOverviewData] = useState<any[]>([]);
  const [currentSection, setCurrentSection] =
    useState<ProfileSectionType>("Events");

  useEffect(() => {
    console.log("ProfileBA received username:", username);
  }, [username]);

  useEffect(() => {
    if (baId) {
      loadAmbassadorProfile();
    }
  }, [baId]);

  const loadAmbassadorProfile = async () => {
    try {
      const response = await axios.get<AmbassadorProfile>(
        `${import.meta.env.VITE_API_URL}/ambassadors/ambassadors/${baId}`
      );
      console.log("Ambassador profile loaded:", response.data);
      const ratings = {
        avg_engagement: response.data.avg_engagement,
        avg_expertise: response.data.avg_expertise,
        avg_overall: response.data.avg_overall,
        avg_professionalism: response.data.avg_professionalism,
        avg_sales: response.data.avg_sales,
      };
      setAmbassadorProfile({ ...response.data, ratings });

      // Fetch sales data
      const salesResponse = await axios.get(
        `${
          import.meta.env.VITE_API_URL
        }/analytics/data_sales_ba_cat?user_id=${baId}`
      );
      const salesData = salesResponse.data;
      console.log("Sales Data:", salesData);
      const data = salesData.map((item: any) => ({
        id: item.category,
        value: item.total_sales,
        label: item.category,
      }));
      setAnalyticsData(data);

      // Fetch sales overview data
      const overviewResponse = await axios.get(
        `${
          import.meta.env.VITE_API_URL
        }/analytics/data_sales_ba?user_id=${baId}`
      );

      const overviewData = overviewResponse.data[0]; // Assuming the response is an array

      // Parse avg_sales_gender if it's a string

      const dashboardData = [
        { label: "# Events", value: overviewData.total_events },
        {
          label: "Total Hours",
          value: parseFloat(overviewData.total_hours).toFixed(0),
        },
        { label: "Product Sold", value: overviewData.total_sales },
        {
          label: "Hourly Sales",
          value: parseFloat(overviewData.sales_per_hour).toFixed(1),
        },
      ];
      setSalesOverviewData(dashboardData); // Update state to hold the new data format
    } catch (error) {
      console.error("Error fetching ambassador profile:", error);
    }
  };

  const renderSection = () => {
    switch (currentSection) {
      case "Events":
        return (
          <Grid item xs={12}>
            <Events
              profileView={{
                type: "BA",
                id: baId,
              }}
            />
          </Grid>
        );
      case "Performance":
        return (
          <Grid item xs={12}>
            <AnalyticsBA
              salesOverviewData={salesOverviewData}
              analyticsData={analyticsData}
            />
          </Grid>
        );
      case "Reviews":
        return (
          <Grid item xs={12}>
            <Paper elevation={3}>
              <Reviews baId={baId} />
            </Paper>
          </Grid>
        );
      case "Photos":
        return (
          <Grid item xs={12}>
            <PhotoLibrary username={username} />
          </Grid>
        );
      default:
        return null;
    }
  };

  const content = (
    <Container maxWidth="lg" sx={{ height: "100%" }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          {ambassadorProfile && (
            <ProfileHeader
              name={`${ambassadorProfile.first_name} ${ambassadorProfile.last_name}`}
              avatar={ambassadorProfile.avatar_url}
              subtext={`${ambassadorProfile.city}, ${ambassadorProfile.state_code}`}
              bio={ambassadorProfile.bio}
              ratings={ambassadorProfile.ratings}
              showRatings={true}
              userId={baId}
              userType="BA"
              currentSection={currentSection}
              onSectionChange={setCurrentSection}
            />
          )}
        </Grid>
        {renderSection()}
      </Grid>
    </Container>
  );

  // Update the Dialog component with fixed dimensions and scrollable content
  return isModal ? (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          height: "90vh",
          width: "90vw",
          maxWidth: "1200px",
          maxHeight: "900px",
          overflowY: "auto",
          border: (theme) => `2px solid ${theme.palette.primary.main}`,
          position: "relative",
        },
      }}
    >
      {onClose && (
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </IconButton>
      )}
      {content}
    </Dialog>
  ) : (
    content
  );
};

export default ProfileBA;
